import React, { useEffect, useState, useCallback } from 'react';
import {
  Calculator, TrendingDown, CheckCircle, ChevronDown, ChevronRight,
  Save, List, Eye, X, Download, BarChart2, Shield, Zap,
  RefreshCw, Info, Star, AlertTriangle, Lock
} from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────
interface MaterialOption {
  qty_id: number;
  mat_category: string;
  display_name: string;
  qty_per_sft: number;
  unit: string;
  wastage_pct: number;
  is_structural: boolean;
  sort_order: number;
  options: BrandOption[];
  default_variant: BrandOption | null;
}
interface BrandOption {
  rate_id: number;
  mat_id: number;
  variant_id: number;
  mat_name: string;
  brand_name: string;
  variant_name: string;
  quality_tier: string;
  rate_per_unit: number;
  unit: string;
}
interface Selection {
  mat_category: string;
  display_name: string;
  mat_id: number;
  variant_id: number;
  brand_name: string;
  qty_per_sft: number;
  unit: string;
  rate_per_unit: number;
  wastage_pct: number;
  is_structural: boolean;
  quality_tier: string;
}

const BLDG_TYPES = [
  {code:'IH',label:'Independent House'},{code:'VI',label:'Villa'},
  {code:'SB',label:'Single Block Apt'},{code:'MB',label:'Multi Block Apt'},
  {code:'HR',label:'High Rise'},
];
const TIER_BADGE: Record<string,string> = {
  ECONOMY:'bg-gray-100 text-gray-700', STANDARD:'bg-blue-100 text-blue-700',
  PREMIUM:'bg-amber-100 text-amber-700', LUXURY:'bg-purple-100 text-purple-700',
  CUSTOM:'bg-teal-100 text-teal-700',
};
const STATUS_COLORS: Record<string,string> = {
  DRAFT:'bg-gray-100 text-gray-600', PRESENTED:'bg-blue-100 text-blue-700',
  ACCEPTED:'bg-green-100 text-green-700', REJECTED:'bg-red-100 text-red-700',
  REVISED:'bg-amber-100 text-amber-700',
};
const inp = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
const fmtCr = (n:any) => {
  if (!n || Number(n)===0) return '₹0';
  const v = Number(n);
  if (v>=10000000) return `₹${(v/10000000).toFixed(2)} Cr`;
  return `₹${(v/100000).toFixed(2)} L`;
};
const fmtN = (n:any) => n ? Number(n).toLocaleString('en-IN') : '0';
const fmtDate = (d?:string) => d ? new Date(d).toLocaleDateString('en-IN') : '—';
const exportCSV = (data:any[], filename:string) => {
  if (!data.length) return;
  const keys = Object.keys(data[0]);
  const csv = [keys.join(','), ...data.map(row =>
    keys.map(k=>`"${String(row[k]??'').replace(/"/g,'""')}"`).join(','))].join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
  a.download = filename; a.click();
};

// ── Material Row Component ─────────────────────────────────────
const MaterialRow = ({
  material, selection, totalSft, onSelect
}: {
  material: MaterialOption;
  selection: Selection | null;
  totalSft: number;
  onSelect: (sel: Selection) => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const selected = selection;
  const totalQty = totalSft * material.qty_per_sft;
  const withWaste = totalQty * (1 + material.wastage_pct/100);
  const lineCost = selected ? Math.round(withWaste * selected.rate_per_unit) : 0;

  const tiers = ['ECONOMY','STANDARD','PREMIUM','LUXURY'];

  return (
    <div className={`border rounded-xl overflow-hidden transition-all
      ${material.is_structural
        ? 'border-blue-200 bg-blue-50/30'
        : 'border-gray-200 bg-white'}`}>

      {/* Header row */}
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer"
        onClick={()=>setExpanded(!expanded)}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-800">{material.display_name}</span>
            {material.is_structural && (
              <span className="flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                <Shield size={10}/> Structural — Quality Locked</span>
            )}
          </div>
          <p className="text-xs text-gray-400 mt-0.5">
            {material.qty_per_sft} {material.unit}/SFT + {material.wastage_pct}% wastage
            = {Math.round(withWaste).toLocaleString()} {material.unit} total
          </p>
        </div>

        {/* Selected brand info */}
        {selected ? (
          <div className="text-right flex-shrink-0">
            <p className="text-sm font-bold text-gray-900">{selected.brand_name}</p>
            <p className="text-xs text-gray-500">₹{Number(selected.rate_per_unit).toLocaleString()}/{selected.unit}</p>
            <p className="text-xs font-semibold text-blue-600">{fmtCr(lineCost)}</p>
          </div>
        ) : (
          <div className="text-right flex-shrink-0">
            <p className="text-xs text-amber-600 font-medium">Not selected</p>
          </div>
        )}
        <ChevronDown size={16} className={`text-gray-400 flex-shrink-0 transition-transform ${expanded?'rotate-180':''}`}/>
      </div>

      {/* Brand options */}
      {expanded && (
        <div className="border-t border-gray-100 px-4 py-3">
          {material.is_structural && (
            <div className="mb-3 flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
              <Lock size={13} className="text-blue-600 mt-0.5 flex-shrink-0"/>
              <p className="text-xs text-blue-700">
                RBD does not compromise on structural material quality.
                All options below meet our minimum quality standard.
                Customer can choose preferred brand.
              </p>
            </div>
          )}
          {/* Group by tier */}
          {tiers.map(tier => {
            const tierOptions = material.options.filter(o=>o.quality_tier===tier);
            if (!tierOptions.length) return null;
            return (
              <div key={tier} className="mb-3">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">{tier}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {tierOptions.map(opt => {
                    const isSelected = selected?.variant_id === opt.variant_id;
                    const optQty = totalSft * material.qty_per_sft * (1+material.wastage_pct/100);
                    const optCost = Math.round(optQty * opt.rate_per_unit);
                    return (
                      <button key={opt.variant_id} type="button"
                        onClick={()=>onSelect({
                          mat_category: material.mat_category,
                          display_name: material.display_name,
                          mat_id: opt.mat_id,
                          variant_id: opt.variant_id,
                          brand_name: opt.brand_name,
                          qty_per_sft: material.qty_per_sft,
                          unit: material.unit,
                          rate_per_unit: opt.rate_per_unit,
                          wastage_pct: material.wastage_pct,
                          is_structural: material.is_structural,
                          quality_tier: opt.quality_tier,
                        })}
                        className={`flex items-center justify-between p-2.5 rounded-lg border-2 text-left transition-all
                          ${isSelected
                            ? 'border-blue-600 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300 bg-white'}`}>
                        <div>
                          <p className={`text-sm font-medium ${isSelected?'text-blue-700':'text-gray-800'}`}>
                            {opt.brand_name}
                          </p>
                          <p className="text-xs text-gray-400">{opt.mat_name}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-gray-700">
                            ₹{Number(opt.rate_per_unit).toLocaleString()}/{opt.unit}
                          </p>
                          <p className="text-xs text-gray-400">{fmtCr(optCost)} total</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Cost Summary Panel ─────────────────────────────────────────
const CostSummary = ({ result, compact=false }: { result:any; compact?:boolean }) => {
  if (!result) return null;
  const cb = result.cost_breakdown;
  const cc = result.competitor_comparison;
  return (
    <div className={`${compact?'':'sticky top-4'} space-y-3`}>
      {/* Total */}
      <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-4 text-white">
        <p className="text-blue-200 text-xs">Total Project Cost</p>
        <p className="text-3xl font-bold mt-1">{fmtCr(cb.total_cost)}</p>
        <p className="text-blue-200 text-xs mt-1">
          ₹{Number(cb.cost_per_sft).toLocaleString()}/SFT · {Number(result.input.total_sft).toLocaleString()} SFT
        </p>
        <div className="mt-3 pt-3 border-t border-blue-500 flex justify-between">
          <div>
            <p className="text-xs text-blue-200">Market Rate</p>
            <p className="text-sm font-bold line-through text-blue-300">{fmtCr(cc.competitor_total)}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-green-300">You Save</p>
            <p className="text-lg font-bold text-green-300">{fmtCr(cc.saving_amount)}</p>
            <p className="text-xs text-green-400">{cc.saving_percentage}% cheaper</p>
          </div>
        </div>
      </div>

      {/* Breakdown */}
      <div className="bg-white rounded-xl border border-gray-200 p-3">
        <p className="text-xs font-bold text-gray-600 mb-2">Cost Breakdown</p>
        {[
          {label:'Materials', val:cb.material_cost, pct:Math.round(cb.material_cost/cb.sub_total*100), color:'bg-blue-500'},
          {label:'Labour',    val:cb.labour_cost,   pct:Math.round(cb.labour_cost/cb.sub_total*100),   color:'bg-green-500'},
          {label:'Machinery', val:cb.machinery_cost,pct:Math.round(cb.machinery_cost/cb.sub_total*100),color:'bg-amber-500'},
          {label:'Overhead',  val:cb.overhead_cost, pct:Math.round(cb.overhead_cost/cb.sub_total*100), color:'bg-gray-400'},
        ].map(s=>(
          <div key={s.label} className="flex items-center gap-2 mb-1.5">
            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${s.color}`}/>
            <span className="text-xs text-gray-600 flex-1">{s.label} ({s.pct}%)</span>
            <span className="text-xs font-semibold text-gray-800">{fmtCr(s.val)}</span>
          </div>
        ))}
        <div className="border-t border-gray-100 pt-1.5 mt-1">
          <div className="flex justify-between text-xs">
            <span className="text-gray-500">Contingency (10%)</span>
            <span className="font-medium">{fmtCr(cb.contingency_amt)}</span>
          </div>
          <div className="flex justify-between text-sm font-bold mt-1">
            <span className="text-gray-800">Total</span>
            <span className="text-blue-600">{fmtCr(cb.total_cost)}</span>
          </div>
        </div>
      </div>

      {/* Why cheaper */}
      <div className="bg-green-50 rounded-xl border border-green-200 p-3">
        <p className="text-xs font-bold text-green-700 mb-2 flex items-center gap-1">
          <TrendingDown size={12}/> Why RBD is cheaper</p>
        {cc.saving_reasons.map((r:string,i:number)=>(
          <div key={i} className="flex items-start gap-1.5 mb-1">
            <CheckCircle size={11} className="text-green-500 mt-0.5 flex-shrink-0"/>
            <span className="text-xs text-green-700">{r}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Save Modal ─────────────────────────────────────────────────
const SaveModal = ({ result, onClose, onSaved }: { result:any; onClose:()=>void; onSaved:(code:string)=>void }) => {
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [leadId, setLeadId]       = useState('');
  const [custNotes, setCustNotes] = useState('');
  const [intNotes, setIntNotes]   = useState('');
  const [leads, setLeads]         = useState<any[]>([]);

  useEffect(() => {
    const loadLeads = async () => {
      try {
        const { default: api } = await import('../../utils/api');
        const r = await api.get('/leads', { params: { status: '' } });
        setLeads(r.data.data || []);
      } catch {}
    };
    loadLeads();
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) { setError('Please select a lead'); return; }
    setSaving(true); setError('');
    try {
      const { default: api } = await import('../../utils/api');
      const payload = {
        bldg_type:          result.input.bldg_type,
        floors:             result.input.floors,
        area_per_floor_sft: result.input.area_per_floor_sft,
        quality_tier:       'STANDARD',
        material_cost:      result.cost_breakdown.material_cost,
        labour_cost:        result.cost_breakdown.labour_cost,
        machinery_cost:     result.cost_breakdown.machinery_cost,
        overhead_cost:      result.cost_breakdown.overhead_cost,
        sub_total:          result.cost_breakdown.sub_total,
        contingency_pct:    10,
        contingency_amount: result.cost_breakdown.contingency_amt,
        total_cost:         result.cost_breakdown.total_cost,
        cost_per_sft:       result.cost_breakdown.cost_per_sft,
        competitor_total:   result.competitor_comparison.competitor_total,
        rbd_saving_amount:  result.competitor_comparison.saving_amount,
        saving_percentage:  result.competitor_comparison.saving_percentage,
        lead_id:            Number(leadId),
        customer_notes:     custNotes||null,
        internal_notes:     intNotes||null,
        material_breakdown: result.material_breakdown,
      };
      const r = await api.post('/estimation/save-detailed', payload);
      onSaved(r.data.data.estimate_code);
    } catch(err:any) { setError(err.response?.data?.message||'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="font-bold text-gray-900">Save Estimate</h3>
            <p className="text-xs text-gray-400">
              {result.input.bldg_type} · {result.input.floors}F ·
              {Number(result.input.total_sft).toLocaleString()} SFT ·
              {fmtCr(result.cost_breakdown.total_cost)}
            </p>
          </div>
          <button onClick={onClose}><X size={18} className="text-gray-400"/></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">{error}</div>}
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Select Lead *</label>
            <select required value={leadId} onChange={e=>setLeadId(e.target.value)} className={inp}>
              <option value="">— Select lead —</option>
              {leads.map((l:any) => (
                <option key={l.lead_id} value={l.lead_id}>
                  {l.lead_code} · {l.full_name} · {l.mobile} · {l.building_interest}
                </option>
              ))}
            </select>
            {leads.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">No leads found. Create a lead first.</p>
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Customer Notes</label>
            <textarea rows={2} value={custNotes} onChange={e=>setCustNotes(e.target.value)}
              placeholder="Visible to customer in proposal..." className={inp}/>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Internal Notes</label>
            <textarea rows={2} value={intNotes} onChange={e=>setIntNotes(e.target.value)}
              placeholder="Internal reference..." className={inp}/>
          </div>
          <p className="text-xs text-gray-400 bg-gray-50 rounded-lg px-3 py-2">
            Valid 90 days · {result.material_breakdown?.length||0} materials · Lead required · Status: DRAFT
          </p>
          <div className="flex gap-3">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
              <Save size={14}/>{saving?'Saving...':'Save Estimate'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Smart Calculator Tab ───────────────────────────────────────
const SmartCalculatorTab = () => {
  // Step 1: Building details
  const [bldgType, setBldgType]   = useState('IH');
  const [floors, setFloors]       = useState(4);
  const [area, setArea]           = useState(1200);
  const [step, setStep]           = useState<1|2|3>(1);

  // Step 2: Material options
  const [materialOptions, setMaterialOptions] = useState<MaterialOption[]>([]);
  const [selections, setSelections]           = useState<Record<string,Selection>>({});
  const [loadingMats, setLoadingMats]         = useState(false);

  // Step 3: Result
  const [result, setResult]       = useState<any>(null);
  const [calculating, setCalc]    = useState(false);
  const [showSave, setShowSave]   = useState(false);
  const [savedCode, setSavedCode] = useState('');

  const totalSft = floors * area;
  const selectedCount  = Object.keys(selections).length;
  const totalCount     = materialOptions.length;

  // Load material options
  const loadMaterials = async () => {
    setLoadingMats(true);
    try {
      const { default: api } = await import('../../utils/api');
      const r = await api.get('/estimation/material-options');
      const opts: MaterialOption[] = r.data.data;
      setMaterialOptions(opts);
      // Auto-select STANDARD tier for all
      const autoSel: Record<string,Selection> = {};
      opts.forEach(mat => {
        const def = mat.options.find(o=>o.quality_tier==='STANDARD') || mat.options[0];
        if (def) {
          autoSel[mat.mat_category] = {
            mat_category: mat.mat_category,
            display_name: mat.display_name,
            mat_id: def.mat_id,
            variant_id: def.variant_id,
            brand_name: def.brand_name,
            qty_per_sft: mat.qty_per_sft,
            unit: mat.unit,
            rate_per_unit: def.rate_per_unit,
            wastage_pct: mat.wastage_pct,
            is_structural: mat.is_structural,
            quality_tier: def.quality_tier,
          };
        }
      });
      setSelections(autoSel);
      setStep(2);
    } finally { setLoadingMats(false); }
  };

  // Calculate in real-time whenever selections change
  const calculate = useCallback(async () => {
    if (Object.keys(selections).length === 0) return;
    setCalc(true);
    try {
      const { default: api } = await import('../../utils/api');
      const r = await api.post('/estimation/calculate-detailed', {
        bldg_type: bldgType, floors, area_per_floor_sft: area,
        selections: Object.values(selections),
      });
      setResult(r.data.data);
    } finally { setCalc(false); }
  }, [selections, bldgType, floors, area]);

  useEffect(() => {
    if (step===2 && Object.keys(selections).length > 0) {
      const timer = setTimeout(calculate, 300); // debounce 300ms
      return () => clearTimeout(timer);
    }
  }, [selections, step, calculate]);

  const reset = () => {
    setStep(1); setResult(null); setSelections({}); setMaterialOptions([]); setSavedCode('');
  };

  // Quick tier preset
  const applyTier = (tier: string) => {
    const newSel: Record<string,Selection> = {};
    materialOptions.forEach(mat => {
      const opt = mat.options.find(o=>o.quality_tier===tier)
               || mat.options.find(o=>o.quality_tier==='STANDARD')
               || mat.options[0];
      if (opt) {
        newSel[mat.mat_category] = {
          mat_category: mat.mat_category, display_name: mat.display_name,
          mat_id: opt.mat_id, variant_id: opt.variant_id,
          brand_name: opt.brand_name, qty_per_sft: mat.qty_per_sft,
          unit: mat.unit, rate_per_unit: opt.rate_per_unit,
          wastage_pct: mat.wastage_pct, is_structural: mat.is_structural,
          quality_tier: opt.quality_tier,
        };
      }
    });
    setSelections(newSel);
  };

  return (
    <div>
      {showSave && result && (
        <SaveModal result={result} onClose={()=>setShowSave(false)}
          onSaved={code=>{ setShowSave(false); setSavedCode(code); }}/>
      )}

      {savedCode && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle size={16} className="text-green-600"/>
            <span className="text-sm text-green-700 font-medium">Saved as {savedCode}</span>
          </div>
          <button onClick={()=>setSavedCode('')}><X size={14} className="text-green-500"/></button>
        </div>
      )}

      {/* Step 1 — Building Details */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
        <h2 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center font-bold">1</span>
          Building Details
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Building Type</label>
            <select value={bldgType} onChange={e=>{setBldgType(e.target.value);setStep(1);setResult(null);}} className={inp}>
              {BLDG_TYPES.map(b=><option key={b.code} value={b.code}>{b.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">No. of Floors</label>
            <input type="number" min={1} max={30} value={floors}
              onChange={e=>{setFloors(Number(e.target.value));setStep(1);setResult(null);}} className={inp}/>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Area per Floor (SFT)</label>
            <input type="number" min={200} value={area}
              onChange={e=>{setArea(Number(e.target.value));setStep(1);setResult(null);}} className={inp}/>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Total built-up: <span className="font-bold text-gray-900">{totalSft.toLocaleString()} SFT</span>
          </p>
          <button onClick={loadMaterials} disabled={loadingMats}
            className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {loadingMats ? 'Loading...' : <>Select Materials <ChevronRight size={14}/></>}
          </button>
        </div>
      </div>

      {/* Step 2 — Material Selection */}
      {step>=2 && materialOptions.length>0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold text-gray-700 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center font-bold">2</span>
              Select Materials
              <span className="text-xs font-normal text-gray-400">({selectedCount}/{totalCount} selected)</span>
            </h2>
            {/* Quick tier presets */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-400">Quick set:</span>
              {['ECONOMY','STANDARD','PREMIUM','LUXURY'].map(t=>(
                <button key={t} type="button" onClick={()=>applyTier(t)}
                  className={`px-2 py-1 text-xs rounded-lg font-medium border transition-all ${TIER_BADGE[t]}`}>
                  {t}</button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Material list — 2/3 width */}
            <div className="lg:col-span-2 space-y-3">
              {/* Structural section */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Shield size={14} className="text-blue-600"/>
                  <p className="text-xs font-bold text-blue-700 uppercase tracking-wide">
                    Structural Materials — RBD Quality Standard</p>
                </div>
                {materialOptions.filter(m=>m.is_structural).map(mat=>(
                  <MaterialRow key={mat.qty_id} material={mat}
                    selection={selections[mat.mat_category]||null}
                    totalSft={totalSft}
                    onSelect={sel=>setSelections(s=>({...s,[mat.mat_category]:sel}))}/>
                ))}
              </div>

              {/* Interior section */}
              <div className="mt-4">
                <div className="flex items-center gap-2 mb-2">
                  <Star size={14} className="text-amber-500"/>
                  <p className="text-xs font-bold text-amber-700 uppercase tracking-wide">
                    Interior & Finishing Materials — Customer Choice</p>
                </div>
                {materialOptions.filter(m=>!m.is_structural).map(mat=>(
                  <MaterialRow key={mat.qty_id} material={mat}
                    selection={selections[mat.mat_category]||null}
                    totalSft={totalSft}
                    onSelect={sel=>setSelections(s=>({...s,[mat.mat_category]:sel}))}/>
                ))}
              </div>
            </div>

            {/* Cost summary — 1/3 width, sticky */}
            <div className="lg:col-span-1">
              {calculating ? (
                <div className="bg-white rounded-xl border border-gray-200 p-6 text-center">
                  <RefreshCw size={20} className="animate-spin text-blue-600 mx-auto mb-2"/>
                  <p className="text-sm text-gray-400">Calculating...</p>
                </div>
              ) : result ? (
                <div>
                  <CostSummary result={result}/>
                  {/* Material breakdown table */}
                  <div className="mt-3 bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                      <p className="text-xs font-bold text-gray-600">Material Cost Breakdown</p>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="text-left px-3 py-1.5 font-medium text-gray-500">Material</th>
                            <th className="text-right px-3 py-1.5 font-medium text-gray-500">Cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.material_breakdown.map((m:any)=>(
                            <tr key={m.mat_category} className="border-b border-gray-50 hover:bg-gray-50">
                              <td className="px-3 py-1.5">
                                <p className="font-medium text-gray-800">{m.display_name}</p>
                                <p className="text-gray-400">{m.brand_name} · ₹{Number(m.rate_per_unit).toLocaleString()}/{m.unit}</p>
                              </td>
                              <td className="px-3 py-1.5 text-right font-semibold text-gray-700">
                                {fmtCr(m.line_cost)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button onClick={()=>setShowSave(true)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700">
                      <Save size={14}/> Save Estimate</button>
                    <button onClick={reset}
                      className="px-3 py-2 text-xs text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50">
                      Reset</button>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-xl border border-dashed border-gray-300 p-6 text-center text-gray-400">
                  <Calculator size={24} className="mx-auto mb-2 opacity-50"/>
                  <p className="text-sm">Select materials to see cost</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Saved Estimates Tab ────────────────────────────────────────
const SavedEstimatesTab = () => {
  const [estimates, setEstimates] = useState<any[]>([]);
  const [stats, setStats]         = useState<any>(null);
  const [loading, setLoading]     = useState(true);
  const [statusFilter, setStatus] = useState('');
  const [detailId, setDetailId]   = useState<number|null>(null);
  const [detailEst, setDetailEst] = useState<any>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { default: api } = await import('../../utils/api');
      const [er, sr] = await Promise.all([
        api.get('/estimation', {params:{status:statusFilter||undefined}}),
        api.get('/estimation/stats'),
      ]);
      setEstimates(er.data.data); setStats(sr.data.data);
    } finally { setLoading(false); }
  };
  useEffect(()=>{ load(); }, [statusFilter]);

  const openDetail = async (id: number) => {
    setDetailId(id); setLoadingDetail(true);
    try {
      const { default: api } = await import('../../utils/api');
      const r = await api.get(`/estimation/${id}`);
      setDetailEst(r.data.data);
    } finally { setLoadingDetail(false); }
  };

  const updateStatus = async (id: number, status: string) => {
    const { default: api } = await import('../../utils/api');
    await api.patch(`/estimation/${id}/status`, {status});
    load();
    if (detailId===id) {
      const r = await api.get(`/estimation/${id}`);
      setDetailEst(r.data.data);
    }
  };

  return (
    <>
      {/* Detail panel */}
      {detailId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-xl max-h-[88vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <div>
                <h3 className="font-bold text-gray-900">{detailEst?.estimate_code||'Loading...'}</h3>
                <p className="text-xs text-gray-400">
                  {detailEst?.bldg_type} · {detailEst?.floors}F ·
                  {detailEst && Number(detailEst.area_per_floor_sft).toLocaleString()} SFT/floor
                  {detailEst?.lead_name && ` · ${detailEst.lead_name}`}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {detailEst && <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[detailEst.status]||''}`}>{detailEst.status}</span>}
                {detailEst && <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TIER_BADGE[detailEst.quality_tier]||''}`}>{detailEst.quality_tier}</span>}
                <button onClick={()=>{setDetailId(null);setDetailEst(null);}}><X size={18} className="text-gray-400"/></button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {loadingDetail ? (
                <div className="text-center py-12 text-gray-400">Loading...</div>
              ) : detailEst && (
                <div className="space-y-4">
                  {/* Cost cards */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-blue-600 rounded-xl p-3 text-white text-center">
                      <p className="text-lg font-bold">{fmtCr(detailEst.total_cost)}</p>
                      <p className="text-xs opacity-80">Total Cost</p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3 text-center">
                      <p className="text-lg font-bold text-gray-800">₹{Number(detailEst.cost_per_sft).toLocaleString()}</p>
                      <p className="text-xs text-gray-500">Per SFT</p>
                    </div>
                    <div className="bg-green-50 rounded-xl p-3 text-center">
                      <p className="text-lg font-bold text-green-700">{fmtCr(detailEst.rbd_saving_amount)}</p>
                      <p className="text-xs text-green-600">Saving vs Market</p>
                    </div>
                  </div>
                  {/* Dates */}
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    {[
                      {label:'Prepared',   val:fmtDate(detailEst.created_at)},
                      {label:'Valid Until', val:fmtDate(detailEst.valid_until)},
                      {label:'Presented',  val:fmtDate(detailEst.presented_on)},
                    ].map(s=>(
                      <div key={s.label} className="bg-gray-50 rounded-lg p-2 text-center">
                        <p className="font-medium text-gray-700">{s.val}</p>
                        <p className="text-gray-400 mt-0.5">{s.label}</p>
                      </div>
                    ))}
                  </div>
                  {/* Material selections */}
                  {detailEst.material_selections && detailEst.material_selections.length>0 && (
                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                      <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                        <p className="text-xs font-bold text-gray-600">Material Selections ({detailEst.material_selections.length})</p>
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="text-left px-3 py-1.5 font-medium text-gray-500">Material</th>
                              <th className="text-left px-3 py-1.5 font-medium text-gray-500">Brand</th>
                              <th className="text-right px-3 py-1.5 font-medium text-gray-500">Rate</th>
                              <th className="text-right px-3 py-1.5 font-medium text-gray-500">Cost</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailEst.material_selections.map((m:any)=>(
                              <tr key={m.selection_id} className="border-b border-gray-50">
                                <td className="px-3 py-1.5 font-medium text-gray-800">{m.display_name}</td>
                                <td className="px-3 py-1.5 text-gray-500">{m.brand_name||'—'}</td>
                                <td className="px-3 py-1.5 text-right">₹{Number(m.rate_per_unit).toLocaleString()}/{m.unit}</td>
                                <td className="px-3 py-1.5 text-right font-semibold text-blue-600">{fmtCr(m.material_cost)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {/* Status workflow */}
                  <div>
                    <p className="text-xs font-bold text-gray-600 mb-2">Update Status</p>
                    <div className="flex flex-wrap gap-2">
                      {['DRAFT','PRESENTED','ACCEPTED','REJECTED','REVISED'].map(s=>(
                        <button key={s} onClick={()=>updateStatus(detailEst.estimate_id,s)}
                          className={`px-3 py-1.5 text-xs rounded-lg border transition-all
                            ${detailEst.status===s?'bg-blue-600 text-white border-blue-600 font-medium':'border-gray-200 text-gray-600 hover:border-gray-400'}`}>
                          {s}{detailEst.status===s?' ✓':''}</button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{estimates.length} estimates</p>
        <button onClick={()=>exportCSV(estimates.map(e=>({
          Code:e.estimate_code,Type:e.bldg_type,Floors:e.floors,
          Area:e.area_per_floor_sft,Tier:e.quality_tier,
          Total:e.total_cost,PerSFT:e.cost_per_sft,
          Status:e.status,ValidUntil:fmtDate(e.valid_until),Lead:e.lead_name||''
        })),'estimates.csv')}
          className="flex items-center gap-1 px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">
          <Download size={14}/> Export</button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-5 gap-3 mb-4">
          {[
            {label:'Total',    val:stats.total,     c:'bg-gray-700 text-white'},
            {label:'Draft',    val:stats.draft,     c:'bg-gray-100 text-gray-700'},
            {label:'Presented',val:stats.presented, c:'bg-blue-100 text-blue-700'},
            {label:'Accepted', val:stats.accepted,  c:'bg-green-100 text-green-700'},
            {label:'Rejected', val:stats.rejected,  c:'bg-red-100 text-red-700'},
          ].map(s=>(
            <div key={s.label} className={`${s.c} rounded-xl p-3 text-center cursor-pointer`}
              onClick={()=>setStatus(statusFilter===(s.label==='Total'?'':s.label.toUpperCase())?'':s.label==='Total'?'':s.label.toUpperCase())}>
              <p className="text-xl font-bold">{fmtN(s.val)}</p>
              <p className="text-xs mt-0.5 opacity-80">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filter */}
      <div className="flex flex-wrap gap-2 mb-4">
        {['','DRAFT','PRESENTED','ACCEPTED','REJECTED','REVISED'].map(s=>(
          <button key={s} onClick={()=>setStatus(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors
              ${statusFilter===s?'bg-blue-600 text-white border-blue-600':'bg-white text-gray-600 border-gray-200'}`}>
            {s||'All'}</button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Estimate</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Building</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Tier</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Total</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Saving</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Status</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Valid</th>
              <th className="px-4 py-3"/>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? [...Array(4)].map((_,i)=>(
              <tr key={i}>{[...Array(8)].map((_,j)=>(
                <td key={j} className="px-4 py-3"><div className="bg-gray-100 rounded h-4 animate-pulse"/></td>
              ))}</tr>
            )) : estimates.length===0 ? (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-400">
                No estimates yet. Use the Smart Calculator to create one.
              </td></tr>
            ) : estimates.map(e=>(
              <tr key={e.estimate_id} className="hover:bg-gray-50 cursor-pointer" onClick={()=>openDetail(e.estimate_id)}>
                <td className="px-4 py-3">
                  <p className="text-xs font-medium text-gray-900">{e.estimate_code}</p>
                  {e.lead_name && <p className="text-xs text-blue-500">{e.lead_name}</p>}
                  {e.material_count>0 && <p className="text-xs text-gray-400">{e.material_count} materials</p>}
                </td>
                <td className="px-4 py-3 text-xs text-gray-600">
                  {e.bldg_type} · {e.floors}F · {Number(e.area_per_floor_sft).toLocaleString()} SFT
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TIER_BADGE[e.quality_tier]||''}`}>{e.quality_tier}</span>
                </td>
                <td className="px-4 py-3 font-semibold text-gray-900">{fmtCr(e.total_cost)}</td>
                <td className="px-4 py-3 text-green-600 text-xs font-medium">
                  {e.rbd_saving_amount?fmtCr(e.rbd_saving_amount):'—'}
                  {e.saving_percentage?` (${e.saving_percentage}%)`:''}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[e.status]||''}`}>{e.status}</span>
                </td>
                <td className="px-4 py-3 text-xs text-gray-400">{fmtDate(e.valid_until)}</td>
                <td className="px-4 py-3" onClick={ev=>ev.stopPropagation()}>
                  <button onClick={()=>openDetail(e.estimate_id)}
                    className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-lg hover:bg-blue-100">
                    <Eye size={11}/> View</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

// ── Main EstimatePage ──────────────────────────────────────────
const EstimatePage = () => {
  const [tab, setTab] = useState<'calculator'|'estimates'>('calculator');
  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <Calculator size={22} className="text-blue-600"/>
          Smart Estimate Engine
        </h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Select materials per category · Real-time cost calculation · RBD vs market comparison
        </p>
      </div>
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit mb-6">
        {([
          {key:'calculator',label:'Smart Calculator',icon:<Calculator size={14}/>},
          {key:'estimates', label:'Saved Estimates', icon:<List size={14}/>},
        ] as const).map(t=>(
          <button key={t.key} onClick={()=>setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-md transition-colors
              ${tab===t.key?'bg-white text-gray-900 shadow-sm':'text-gray-500 hover:text-gray-700'}`}>
            {t.icon}{t.label}</button>
        ))}
      </div>
      {tab==='calculator' && <SmartCalculatorTab/>}
      {tab==='estimates'  && <SavedEstimatesTab/>}
    </div>
  );
};

export default EstimatePage;
